import { RefObject, useEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import styled from "styled-components"

import { useOutsideClick } from "utils/useOutsideClick"

import { CustomDatePickerPopover } from "./CustomDatePickerPopover"
import { DATE_RANGE_FILTER_GROUPS, getDateRangeParams, getFilterValueFromParams } from "./dateRangeFilterUtils"

const BORDER_COLOR = "#e2e8f0"

const FiltersWrapper = styled.div`
  display: inline-flex;
  flex-wrap: wrap;
  gap: 1px;

  background-color: ${BORDER_COLOR};
  border: 1px solid ${BORDER_COLOR};
  border-radius: 6px;
  margin: 8px 0;
  position: relative;
  overflow: hidden;
`

const FilterGroup = styled.div`
  display: flex;
  flex: 1;
  gap: 1px;
  background-color: ${BORDER_COLOR};
`

const CustomTabWrapper = styled.div`
  position: relative;
  flex: 1;
`

const TableFilter = styled.div<{ $active: boolean }>`
  display: flex;
  flex: 1;
  position: relative;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  white-space: nowrap;
  cursor: pointer;
  background-color: var(--surface-1);

  span {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: var(--text-secondary);
  }

  ${({ $active }) =>
    $active
      ? `
    background-color: #aff2fd;
  `
      : `
    &:hover {
      background-color: var(--surface-2);
    }
  `}
`

interface DateRangeTabsFilterProps {
  paramName: string
  urlQueryParams: Record<string, string>
  onParamsChange: (params: Record<string, string | null>) => void
  filterValues?: readonly string[]
  filterGroups?: readonly (readonly string[])[]
  onFilterChange?: (value: string) => void
}

export const DateRangeTabsFilter = ({
  paramName,
  urlQueryParams,
  onParamsChange,
  filterValues,
  filterGroups: selectedFilterGroups,
  onFilterChange,
}: DateRangeTabsFilterProps) => {
  const [activeDateFilter, setActiveDateFilter] = useState("all")
  const [isDateCustom, setIsDateCustom] = useState(false)
  const [customFilterDateRange, setCustomFilterDateRange] = useState<{
    startDate: Date
    endDate: Date | null
  }>({
    startDate: new Date(),
    endDate: null,
  })
  const datePickerRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)
  const customTabRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null)

  useOutsideClick(datePickerRef, () => setIsDateCustom(false), false, { preserveClick: true })

  useEffect(() => {
    const filterValue = getFilterValueFromParams(urlQueryParams, paramName)
    setActiveDateFilter(filterValue)
    const startKey = `${paramName}_start`
    const endKey = `${paramName}_end`
    if (filterValue === "custom" && urlQueryParams[startKey] && urlQueryParams[endKey]) {
      setCustomFilterDateRange({
        startDate: dayjs(urlQueryParams[startKey]).toDate(),
        endDate: dayjs(urlQueryParams[endKey]).toDate(),
      })
    }
  }, [urlQueryParams[`${paramName}_start`], urlQueryParams[`${paramName}_end`], paramName])

  const handleChangeDateFilter = (date: string) => {
    setActiveDateFilter(date)
    onFilterChange?.(date)

    if (date === "custom" && !isDateCustom) {
      setIsDateCustom(true)
      return
    }

    onParamsChange(getDateRangeParams(date, paramName))
  }

  const handleCancelDateFilter = () => {
    setIsDateCustom(false)
    setCustomFilterDateRange({
      startDate: new Date(),
      endDate: null,
    })
  }

  const handleApplyDateFilter = (dates?: [Date | null, Date | null]) => {
    const [start, end] = dates ?? [customFilterDateRange.startDate, customFilterDateRange.endDate]
    onParamsChange(getDateRangeParams("custom", paramName, start ?? null, end ?? null))
    setIsDateCustom(false)
  }

  const renderFilter = (filter: (typeof DATE_RANGE_FILTER_GROUPS)[number][number]) =>
    filter.value === "custom" ? (
      <CustomTabWrapper key={filter.id} ref={customTabRef}>
        <TableFilter
          $active={activeDateFilter === filter.value}
          onClick={() => handleChangeDateFilter(filter.value)}
          onKeyDown={e => e.key === "Enter" && handleChangeDateFilter(filter.value)}
          role="button"
          tabIndex={0}
        >
          <span>{filter.label}</span>
        </TableFilter>
        {isDateCustom && (
          <CustomDatePickerPopover
            startDate={customFilterDateRange.startDate}
            endDate={customFilterDateRange.endDate}
            onCancel={handleCancelDateFilter}
            onApply={handleApplyDateFilter}
            wrapperRef={datePickerRef}
            anchorRef={customTabRef}
          />
        )}
      </CustomTabWrapper>
    ) : (
      <TableFilter
        key={filter.id}
        $active={activeDateFilter === filter.value}
        onClick={() => handleChangeDateFilter(filter.value)}
        onKeyDown={e => e.key === "Enter" && handleChangeDateFilter(filter.value)}
        role="button"
        tabIndex={0}
      >
        <span>{filter.label}</span>
      </TableFilter>
    )

  const availableFilters = DATE_RANGE_FILTER_GROUPS.flat()
  let filterGroups

  if (selectedFilterGroups) {
    filterGroups = selectedFilterGroups.map(group => availableFilters.filter(filter => group.includes(filter.value)))
  } else if (filterValues) {
    filterGroups = [availableFilters.filter(filter => filterValues.includes(filter.value))]
  } else {
    filterGroups = DATE_RANGE_FILTER_GROUPS
  }

  return (
    <div>
      <FiltersWrapper>
        {filterGroups.map((group, groupIndex) => (
          <FilterGroup key={groupIndex}>{group.map(filter => renderFilter(filter))}</FilterGroup>
        ))}
      </FiltersWrapper>
    </div>
  )
}
